import {
    FONT_FAMILY,
    FONT_SIZE,
    FONT_WEIGHT,
    LINE_HEIGHT,
    TYPOGRAPHY_STYLES as BASE_TYPOGRAPHY,
    SPACING,
    PADDING,
    MARGIN,
    GAP,
    COMPONENT_SIZES,
    Z_INDEX,
    SHADOWS,
    BORDERS,
    BORDER_RADIUS,
    ELEVATION_STYLES,
    BUTTON_BORDER_STYLES,
    TRANSITION_TIMING,
    TRANSITION_EASING,
    TRANSITIONS,
    ANIMATION_TIMING,
    ANIMATIONS,
    BREAKPOINTS_VALUES,
    MEDIA_QUERIES,
    ICON_SIZES,
    ICON_STROKE_WIDTH,
} from "../definitions";

const DARK_THEME_COLORS = {
    primary: "#0d1117",
    secondary: "#e0e6ed",
    accent: "#64b5f6",
    warning: "#ffb74d",
    discount: "#ffb74d",

    white: "#ffffff",
    black: "#000000",

    greyLight: "#30363d",
    greyMedium: "#444c56",
    greyDark: "#6e7681",
    greyExtraDark: "#21262d",

    success: "#58a637",
    error: "#f85149",
    info: "#64b5f6",

    overlay: {
        dark: "#00000033",
        darkStrong: "#00000066",
        light: "#00000099",
        veryLight: "#00000008",
    },

    text: {
        primary: "#e0e6ed",
        secondary: "#a0a9b3",
        disabled: "#6e7681",
        light: "rgba(224, 230, 237, 0.80)",
        lighter: "rgba(224, 230, 237, 0.70)",
    },

    background: {
        paper: "#161b22",
        default: "#0d1117",
    },
} as const;

const DARK_THEME_TYPOGRAPHY = {
    ...BASE_TYPOGRAPHY,
    rubik: {
        fontFamily: FONT_FAMILY.primary,
        color: DARK_THEME_COLORS.text.primary,
    },
    rubikSmall: {
        fontFamily: FONT_FAMILY.primary,
        fontSize: FONT_SIZE.sm,
        color: DARK_THEME_COLORS.text.primary,
    },
    rubikMedium: {
        fontFamily: FONT_FAMILY.primary,
        fontSize: FONT_SIZE.base,
        color: DARK_THEME_COLORS.text.primary,
    },
    rubikLarge: {
        fontFamily: FONT_FAMILY.primary,
        fontSize: FONT_SIZE.lg,
        color: DARK_THEME_COLORS.text.primary,
    },
    rubikXL: {
        fontFamily: FONT_FAMILY.primary,
        fontSize: FONT_SIZE.xl,
        color: DARK_THEME_COLORS.text.primary,
    },
    rubik2XL: {
        fontFamily: FONT_FAMILY.primary,
        fontSize: FONT_SIZE["2xl"],
        color: DARK_THEME_COLORS.text.primary,
    },
    rubik3XL: {
        fontFamily: FONT_FAMILY.primary,
        fontSize: FONT_SIZE["3xl"],
        color: DARK_THEME_COLORS.text.primary,
    },
    heading1: {
        fontFamily: FONT_FAMILY.primary,
        fontSize: FONT_SIZE.h1,
        fontWeight: FONT_WEIGHT.bold,
        lineHeight: LINE_HEIGHT.tight,
        color: DARK_THEME_COLORS.text.primary,
    },
    heading2: {
        fontFamily: FONT_FAMILY.primary,
        fontSize: FONT_SIZE.h2,
        fontWeight: FONT_WEIGHT.bold,
        lineHeight: LINE_HEIGHT.tight,
        color: DARK_THEME_COLORS.text.primary,
    },
    heading3: {
        fontFamily: FONT_FAMILY.primary,
        fontSize: FONT_SIZE.h3,
        fontWeight: FONT_WEIGHT.bold,
        lineHeight: LINE_HEIGHT.tight,
        color: DARK_THEME_COLORS.text.primary,
    },
    body: {
        fontFamily: FONT_FAMILY.primary,
        fontSize: FONT_SIZE.base,
        lineHeight: LINE_HEIGHT.normal,
        color: DARK_THEME_COLORS.text.primary,
    },
    bodySmall: {
        fontFamily: FONT_FAMILY.primary,
        fontSize: FONT_SIZE.sm,
        lineHeight: LINE_HEIGHT.normal,
        color: DARK_THEME_COLORS.text.primary,
    },
    bodyLarge: {
        fontFamily: FONT_FAMILY.primary,
        fontSize: FONT_SIZE.lg,
        lineHeight: LINE_HEIGHT.normal,
        color: DARK_THEME_COLORS.text.primary,
    },
    caption: {
        fontFamily: FONT_FAMILY.primary,
        fontSize: FONT_SIZE.xs,
        lineHeight: LINE_HEIGHT.tight,
        color: DARK_THEME_COLORS.text.primary,
    },
    label: {
        fontFamily: FONT_FAMILY.primary,
        fontSize: FONT_SIZE.sm,
        fontWeight: FONT_WEIGHT.bold,
        textTransform: "uppercase" as const,
        lineHeight: LINE_HEIGHT.normal,
        color: DARK_THEME_COLORS.text.primary,
    },
    labelMedium: {
        fontFamily: FONT_FAMILY.primary,
        fontSize: FONT_SIZE.base,
        fontWeight: FONT_WEIGHT.bold,
        textTransform: "uppercase" as const,
        color: DARK_THEME_COLORS.text.primary,
    },
};

export const THEME_DARK = {
    name: "dark",
    label: "Dark Mode",

    colors: DARK_THEME_COLORS,

    typography: {
        fontFamily: FONT_FAMILY,
        fontSize: FONT_SIZE,
        fontWeight: FONT_WEIGHT,
        lineHeight: LINE_HEIGHT,
        styles: DARK_THEME_TYPOGRAPHY,
    },

    spacing: {
        values: SPACING,
        padding: PADDING,
        margin: MARGIN,
        gap: GAP,
    },

    components: {
        sizes: COMPONENT_SIZES,
        zIndex: Z_INDEX,
    },

    visual: {
        shadows: SHADOWS,
        borders: BORDERS,
        borderRadius: BORDER_RADIUS,
        elevation: ELEVATION_STYLES,
        buttonBorders: BUTTON_BORDER_STYLES,
    },

    animation: {
        timing: TRANSITION_TIMING,
        easing: TRANSITION_EASING,
        transitions: TRANSITIONS,
        animationTiming: ANIMATION_TIMING,
        animations: ANIMATIONS,
    },

    responsive: {
        breakpoints: BREAKPOINTS_VALUES,
        mediaQueries: MEDIA_QUERIES,
    },

    icons: {
        sizes: ICON_SIZES,
        strokeWidth: ICON_STROKE_WIDTH,
    },
} as const;

export type ThemeDark = typeof THEME_DARK;

export { DARK_THEME_COLORS };
